export const AUTH_COOKIE_NAME = process.env.REACT_APP_AUTH_COOKIE_NAME || 'ps_auth_token';
export const AUTH_REFRESH_COOKIE_NAME = process.env.REACT_APP_AUTH_REFRESH_COOKIE_NAME || 'ps_auth_refresh_token';
export const PROBESTACK_LOGIN_URL = process.env.REACT_APP_PROBESTACK_LOGIN_URL || 'https://probestack.io/login';

// Local-dev escape hatch: REACT_APP_SKIP_AUTH=true (in .env.local) skips the
// SSO check and login redirects. Ignored in production builds.
export const SKIP_AUTH =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_SKIP_AUTH === 'true';

const readCookie = (name) => {
  if (typeof document === 'undefined') return '';

  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : '';
};

// document.cookie deletion only takes effect when Domain/Path match exactly
// what the cookie was set with, and we don't control that from here — this
// SSO cookie is shared platform-wide (console./community./support.probestack.io
// all read it, per its ps_auth_* name), set with some ancestor-domain scope
// we can't see. So expire it under every scope it could plausibly have: the
// bare host, no explicit Domain attribute, and each ancestor domain up to
// (but not including) the public suffix (forgeaigateway.probestack.io ->
// .probestack.io), covering whichever one the real Domain attribute used.
const expireCookieEverywhere = (name) => {
  if (typeof document === 'undefined') return;

  const host = window.location.hostname;
  const labels = host.split('.');
  const domainsToTry = [null]; // null = no Domain attribute (defaults to the exact current host)
  for (let i = 1; i < labels.length - 1; i++) {
    domainsToTry.push(`.${labels.slice(i).join('.')}`);
  }

  domainsToTry.forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
  });
};

// Best-effort client-side clear of the shared SSO session cookies on sign
// out. Won't touch an HttpOnly cookie (only the issuing server can clear
// that — see the "logout endpoint" note on logout() below), but this cookie
// has to be JS-readable already (isAuthenticated/getAuthToken below both
// read it via document.cookie), so it isn't one.
export const clearAuthCookies = () => {
  expireCookieEverywhere(AUTH_COOKIE_NAME);
  expireCookieEverywhere(AUTH_REFRESH_COOKIE_NAME);
};

// The shared SSO cookie isn't always readable on this host (HttpOnly, or
// scoped to another domain), so also accept the token the login redirect
// hands back via ?authToken= (stored by hydrateAuthContextFromUrl in
// index.js). Checking the cookie alone bounced users straight back to login
// after a successful sign-in, which then redirected here again — an endless
// redirect loop. The cookie still wins when present so a fresh SSO session
// replaces a stale stored token.
export const getAuthToken = () => {
  const cookieToken = readCookie(AUTH_COOKIE_NAME).trim();
  if (cookieToken) return cookieToken;

  try {
    return (localStorage.getItem('authToken') || '').trim();
  } catch {
    return '';
  }
};

// Set once the admin-backend /me check (App.jsx) confirms the session. The
// login site now keeps the session only in an HttpOnly cookie and no longer
// hands a token back in the URL, so after a normal login neither the cookie
// nor localStorage is visible to JS — without this flag every check here
// failed and bounced the user back to login in an endless loop.
let sessionVerified = false;
export const markSessionVerified = () => {
  sessionVerified = true;
};

export const isAuthenticated = () => SKIP_AUTH || sessionVerified || Boolean(getAuthToken());

export const getLoginRedirectUrl = () => {
  const loginUrl = new URL(PROBESTACK_LOGIN_URL);
  loginUrl.searchParams.set('returnTo', window.location.href);
  return loginUrl.toString();
};

// Sends the user back to login. Safe to call repeatedly (e.g. from
// multiple interceptors/effects) since the navigation itself unmounts the app.
export const redirectToLogin = () => {
  if (SKIP_AUTH) return;
  window.location.replace(getLoginRedirectUrl());
};
